import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BoxCard from '../components/BoxCard';
import BoxModal from '../components/BoxModal';
import { partyBoxes } from '../data/partyBoxes';
import { services } from '../data/services';

const Home = () => {
  const [selectedBox, setSelectedBox] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const featuredBoxes = partyBoxes.slice(0, 6);
  const featuredServices = services.slice(0, 4);

  const handleViewDetails = (box) => {
    setSelectedBox(box);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBox(null);
  };

  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-decoration hero-decoration-1">✨</div>
        <div className="hero-decoration hero-decoration-2">🌟</div>
        
        <div className="hero-content">
          <span className="hero-badge">Curated Party Styling</span>
          <h1>Curated Party Vibes, <br/>Delivered to Your Door</h1>
          <p className="hero-subtitle">
            From baby showers to bachelorettes, Midnight Aurelia Events creates 
            Pinterest-worthy celebrations with expert styling consultations 
            and ready-to-style party boxes.
          </p>
          <div className="hero-cta">
            <Link to="/party-boxes" className="btn btn-primary btn-lg">
              Shop Party Boxes
            </Link>
            <Link to="/services" className="btn btn-secondary btn-lg">
              View Services
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="scroll-indicator">
          <div className="scroll-arrow">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 13l5 5 5-5M7 6l5 5 5-5"/>
            </svg>
          </div>
        </div>
      </section>

      {/* How It Works Section - MOVED ABOVE Shop Signature Boxes */}
      <section className="how-it-works">
        <div className="container">
          <div className="section-header">
            <h2>How It Works</h2>
            <div className="section-divider"></div>
            <p>
              Three simple steps to your perfect party
            </p>
          </div>

          <div className="steps-grid">
            <div className="step">
              <div className="step-number">1</div>
              <h4>Pick Your Vibe</h4>
              <p>
                Choose your favorite party box or take our quiz to find the 
                perfect match for your celebration style.
              </p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h4>Unbox the Magic</h4>
              <p>
                We ship curated, aesthetic-forward decor and hosting tools 
                directly to your door, beautifully packaged.
              </p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h4>Set It Up</h4>
              <p>
                Follow our easy setup guide and transform your space in under 
                an hour. No stress, no tackiness.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Boxes Section */}
      <section className="featured-boxes">
        <div className="container">
          <div className="section-header">
            <h2>Shop Our Signature Boxes</h2>
            <div className="section-divider"></div>
            <p>
              Ready-to-style kits for beautifully themed gatherings — curated by 
              Midnight Aurelia Events, designed to be set up at home.
            </p>
          </div>
          
          <div className="boxes-grid">
            {featuredBoxes.map((box) => (
              <BoxCard 
                key={box.id} 
                box={box} 
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          <div className="text-center mt-2xl">
            <Link to="/party-boxes" className="btn btn-gold btn-lg">
              View All Party Boxes
            </Link>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="services">
        <div className="container">
          <div className="section-header">
            <h2>Our Services</h2>
            <div className="section-divider"></div>
            <p>
              From light-touch consultations to full-service event styling, 
              we have a package for every host and every budget.
            </p>
          </div>

          <div className="services-grid">
            {featuredServices.map((service) => (
              <div key={service.id} className="service-card">
                <div className="service-icon">{service.icon}</div>
                <h3>{service.name}</h3>
                <p>{service.shortDesc}</p>
                <p className="service-price">Starting at {service.startingPrice}</p>
                <Link to="/services" className="btn btn-outline-gold btn-sm mt-md">
                  Learn More
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-2xl">
            <Link to="/services" className="btn btn-primary btn-lg">
              Explore All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="container">
          <div className="section-header">
            <h2>What Our Clients Say</h2>
            <div className="section-divider"></div>
            <p>
              Real hosts, real parties, real magic
            </p>
          </div>

          <div className="testimonials-grid">
            <div className="testimonial-card">
              <p className="testimonial-quote">
                "The Soft Launch box made my baby shower absolutely perfect. 
                Everything was so thoughtfully curated — my guests couldn't 
                believe I styled it myself!"
              </p>
              <div className="testimonial-author">
                <img 
                  src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80" 
                  alt="Sarah M." 
                  className="testimonial-avatar"
                />
                <div className="testimonial-info">
                  <h5>Sarah M.</h5>
                  <span>Baby Shower Host</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <p className="testimonial-quote">
                "Midnight Aurelia Events took all the stress out of planning my 30th. 
                The Dirty Thirty box was exactly the vibe I wanted — elegant, 
                grown-up, and Instagram-worthy."
              </p>
              <div className="testimonial-author">
                <img 
                  src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80" 
                  alt="Jessica R." 
                  className="testimonial-avatar"
                />
                <div className="testimonial-info">
                  <h5>Jessica R.</h5>
                  <span>Birthday Celebrant</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <p className="testimonial-quote">
                "I used The Glow Guide for my sister's bridal shower and it was 
                worth every penny. The moodboard and timeline made everything 
                so much easier to execute."
              </p>
              <div className="testimonial-author">
                <img 
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80" 
                  alt="Amanda K." 
                  className="testimonial-avatar"
                />
                <div className="testimonial-info">
                  <h5>Amanda K.</h5>
                  <span>Bridal Shower Planner</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="instagram-section">
        <div className="container">
          <div className="section-header">
            <h2>Follow the Midnight Moments</h2>
            <div className="section-divider"></div>
            <p>
              @midnightaureliaevents — See how our clients style their celebrations
            </p>
          </div>

          <div className="instagram-grid">
            {[
              'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&q=80',
              'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=400&q=80',
              'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=400&q=80',
              'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=400&q=80',
              'https://images.unsplash.com/photo-1478146896981-b80fe463b330?w=400&q=80',
              'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=400&q=80'
            ].map((img, idx) => (
              <a 
                key={idx} 
                href="https://instagram.com/midnightaureliaevents" 
                target="_blank" 
                rel="noopener noreferrer"
                className="instagram-item"
              >
                <img src={img} alt={`Instagram post ${idx + 1}`} loading="lazy" />
              </a>
            ))}
          </div>

          <div className="text-center mt-xl">
            <a 
              href="https://instagram.com/midnightaureliaevents" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-outline-gold btn-lg"
            >
              Follow @midnightaureliaevents
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Party Differently?</h2>
          <p>
            For the girlies and gays who don't want to email 9 florists or 
            pretend they know what a timeline is. You've got good taste, 
            a limited budget, and decision fatigue. Let's fix that.
          </p>
          <div className="cta-buttons">
            <Link to="/party-boxes" className="btn btn-primary btn-lg">
              Shop Party Boxes
            </Link>
            <Link to="/contact" className="btn btn-secondary btn-lg">
              Book a Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* Box Detail Modal */}
      <BoxModal 
        box={selectedBox}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default Home;
